import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { toast } from "react-toastify";
import { apiget, apipostFormData } from "../../service/api";

const MAX_LOGO_BYTES = 5 * 1024 * 1024; // 5MB — mirrors the server-side multer limit
const ACCEPTED_TYPES = "image/png,image/jpeg,image/webp,image/svg+xml";

/**
 * Shared builder-logo picker backed by the /api/logo library.
 * `onChange` receives the logo URL string, preserving the existing
 * BuilderIntake.logoSrc contract (full Cloudinary URL saved verbatim).
 */
export default function LogoSelect({
  value = "",
  onChange,
  label = "Builder Logo",
  name = "logoSrc",
}) {
  const [logos, setLogos] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newFile, setNewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const fetchLogos = useCallback(async () => {
    const res = await apiget("logo/");
    setLogos(res?.data?.result || []);
  }, []);

  useEffect(() => {
    fetchLogos();
  }, [fetchLogos]);

  // Revoke the object URL whenever it changes or on unmount
  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl]
  );

  const isLegacyValue = value && !logos.some((l) => l.url === value);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setNewFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setNewName("");
    setNewFile(null);
    setPreviewUrl("");
  };

  const handleUpload = async () => {
    const trimmedName = newName.trim();

    // Client-side validation — apipostFormData masks server 4xx messages
    // behind a generic toast, so catch the obvious problems here.
    if (!trimmedName) {
      toast.error("Please enter a name for the logo");
      return;
    }
    if (!newFile) {
      toast.error("Please choose a logo image");
      return;
    }
    if (newFile.size > MAX_LOGO_BYTES) {
      toast.error("Logo must be under 5MB");
      return;
    }
    if (logos.some((l) => l.name.toLowerCase() === trimmedName.toLowerCase())) {
      toast.error("A logo with this name already exists");
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("name", trimmedName);
      fd.append("logo", newFile);

      const res = await apipostFormData("logo/", fd);

      if (res?.status === 201 && res.data?.logo) {
        toast.success("Logo uploaded successfully");
        setLogos((prev) =>
          [...prev, res.data.logo].sort((a, b) => a.name.localeCompare(b.name))
        );
        onChange(res.data.logo.url);
        closeDialog();
      } else if (!res) {
        toast.error("Logo upload failed. Please try again.");
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <TextField
        select
        fullWidth
        label={label}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        helperText={logos.length === 0 && !value ? "No logos yet — upload one below." : undefined}
      >
        <MenuItem value="">
          <em>Select a logo</em>
        </MenuItem>
        {isLegacyValue && (
          <MenuItem value={value}>
            <Box
              component="img"
              src={value}
              alt=""
              sx={{ height: 24, width: 40, objectFit: "contain", mr: 1 }}
            />
            Current logo (legacy)
          </MenuItem>
        )}
        {logos.map((logo) => (
          <MenuItem key={logo._id} value={logo.url}>
            <Box
              component="img"
              src={logo.url}
              alt=""
              sx={{ height: 24, width: 40, objectFit: "contain", mr: 1 }}
            />
            {logo.name}
          </MenuItem>
        ))}
      </TextField>

      <Button
        size="small"
        startIcon={<CloudUploadIcon />}
        onClick={() => setDialogOpen(true)}
        sx={{ mt: 1 }}
      >
        Upload New Logo
      </Button>

      <Dialog open={dialogOpen} onClose={uploading ? undefined : closeDialog} fullWidth maxWidth="xs">
        <DialogTitle>Upload New Logo</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Logo Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            sx={{ mt: 1 }}
          />
          <Button component="label" variant="outlined" fullWidth sx={{ mt: 2 }}>
            {newFile ? newFile.name : "Choose Image"}
            <input type="file" hidden accept={ACCEPTED_TYPES} onChange={handleFileChange} />
          </Button>
          {previewUrl && (
            <Box
              component="img"
              src={previewUrl}
              alt="Logo preview"
              sx={{
                mt: 2,
                width: "100%",
                maxHeight: 120,
                objectFit: "contain",
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={uploading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleUpload} disabled={uploading}>
            {uploading ? "Uploading…" : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
